/**
 * HorizontalMode - Horizontal scrolling reader mode
 * Location: /app/frontend/src/components/page-builder/reader/modes/
 * 
 * Phase 3-B: Enhanced with desktop wheel scroll, mobile swipe, scroll snap
 * - Desktop: Horizontal scroll with mouse wheel + arrow keys
 * - Mobile landscape: Swipe gestures
 * - Mobile portrait: Hint to rotate
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useReaderContext, READER_MODES } from '../hooks/useReaderContext';
import { sanitizeHtml, textToHtml } from '../hooks/sanitizeHtml';
import { Loader2, ImageOff, FileText, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { Button } from '../../../ui/button';

const HorizontalMode = () => {
  const { 
    content, 
    metadata, 
    isLoading, 
    error, 
    contentType,
    isMobile,
    isLandscape,
    setMode,
    setProgress,
    currentPage,
    setCurrentPage,
    totalPages,
    shellSettings,
  } = useReaderContext();
  
  const scrollContainerRef = useRef(null);
  const [showControls, setShowControls] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  // For novels in horizontal mode, switch to page mode
  useEffect(() => {
    if (contentType === 'novel' && !isLoading && content) {
      // Show message and auto-switch after delay
      // Or just handle it inline with paged text
    }
  }, [contentType, isLoading, content, setMode]);

  // Mouse wheel handler - translate vertical scroll to horizontal
  const handleWheel = useCallback((e) => {
    if (!scrollContainerRef.current) return;
    
    // Prevent default vertical scroll
    e.preventDefault();
    
    // Scroll horizontally
    const container = scrollContainerRef.current;
    const scrollAmount = e.deltaY > 0 ? 400 : -400;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    
    switch (e.key) {
      case 'ArrowRight':
      case ' ':
        e.preventDefault();
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        break;
      case 'ArrowLeft':
        e.preventDefault();
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        break;
      default:
        break;
    }
  }, []);

  // Touch handlers for mobile swipe (controlled by enableSwipeNav)
  const handleTouchStart = useCallback((e) => {
    if (shellSettings?.enableSwipeNav === false) return;
    setTouchStart(e.touches[0].clientX);
  }, [shellSettings?.enableSwipeNav]);

  const handleTouchEnd = useCallback((e) => {
    if (shellSettings?.enableSwipeNav === false) return;
    if (touchStart === null || !scrollContainerRef.current) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    const threshold = 50;
    
    if (Math.abs(diff) > threshold) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth * 0.9;
      
      if (diff > 0) {
        // Swipe left = scroll right
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      } else {
        // Swipe right = scroll left
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
    }
    
    setTouchStart(null);
  }, [touchStart]);

  // Track scroll position for progress
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const maxScroll = scrollWidth - clientWidth;
    const percent = maxScroll > 0 ? Math.round((scrollLeft / maxScroll) * 100) : 0;
    
    setProgress({ percent, scrollX: scrollLeft }, true);
  }, [setProgress]);

  // Attach listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      container.addEventListener('scroll', handleScroll, { passive: true });
      if (shellSettings?.enableKeyboardNav !== false) {
        window.addEventListener('keydown', handleKeyDown);
      }
      
      return () => {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleWheel, handleScroll, handleKeyDown, shellSettings?.enableKeyboardNav]);

  // Navigation buttons
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ 
        left: -scrollContainerRef.current.clientWidth * 0.8, 
        behavior: 'smooth' 
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ 
        left: scrollContainerRef.current.clientWidth * 0.8, 
        behavior: 'smooth' 
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
        <ImageOff className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
        <FileText className="w-16 h-16 text-zinc-600 mb-4" />
        <p className="text-zinc-400">No content available</p>
      </div>
    );
  }

  // Show rotate hint on mobile portrait
  if (isMobile && !isLandscape && contentType === 'comic') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
        <RotateCcw className="w-16 h-16 text-orange-500 mb-4 animate-pulse" />
        <p className="text-white font-medium mb-2">Rotate for Best Experience</p>
        <p className="text-zinc-400 text-sm mb-4">Horizontal mode works best in landscape orientation</p>
        <Button 
          onClick={() => setMode(READER_MODES.VERTICAL)}
          variant="outline" 
          className="mt-4 border-zinc-700 text-zinc-300"
        >
          Switch to Vertical Mode
        </Button>
      </div>
    );
  }

  // Comic: horizontal scroll of pages
  if (contentType === 'comic' && content.pages) {
    return (
      <div 
        className="horizontal-reader relative h-[calc(100vh-56px)]"
        data-testid="horizontal-mode-comic"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Navigation buttons (desktop hover) */}
        {!isMobile && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 w-12 h-12 rounded-full transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'}`}
              onClick={scrollLeft}
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 w-12 h-12 rounded-full transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'}`}
              onClick={scrollRight}
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </Button>
          </>
        )}

        {/* Horizontal scroll container with snap */}
        <div 
          ref={scrollContainerRef}
          className="flex h-full overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <style>{`.horizontal-reader ::-webkit-scrollbar { display: none; }`}</style>
          
          {content.pages.map((page, index) => (
            <div 
              key={page.id || index} 
              className="flex-shrink-0 h-full snap-center flex items-center justify-center px-2"
              data-page-index={index}
              style={{ minWidth: isMobile ? '100vw' : 'auto' }}
            >
              {page.image_url || page.url || page.src ? (
                <img
                  src={page.image_url || page.url || page.src}
                  alt={`Page ${index + 1}`}
                  className="max-h-full w-auto object-contain"
                  loading={index < 5 ? 'eager' : 'lazy'}
                />
              ) : (
                <div className="h-[80vh] w-[60vw] max-w-lg bg-zinc-800 flex items-center justify-center rounded-lg">
                  <ImageOff className="w-12 h-12 text-zinc-600" />
                </div>
              )}
            </div>
          ))}
          
          {/* End spacer */}
          <div className="flex-shrink-0 w-32" />
        </div>

        {/* Page indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full">
          <span className="text-white text-sm">
            {content.pages.length} pages • {isMobile ? 'Swipe' : 'Scroll or use arrow keys'}
          </span>
        </div>
      </div>
    );
  }

  // Novel: horizontal mode shows paged text or switches to page mode
  if (contentType === 'novel' && (content.text || content.contentHtml)) {
    // For novels, show text in horizontal columns
    let htmlContent = '';
    if (content.contentHtml) {
      htmlContent = sanitizeHtml(content.contentHtml);
    } else if (content.text) {
      const hasHtml = /<[a-z][\s\S]*>/i.test(content.text);
      htmlContent = hasHtml ? sanitizeHtml(content.text) : textToHtml(content.text);
    }

    return (
      <div 
        className="horizontal-reader relative h-[calc(100vh-56px)]"
        data-testid="horizontal-mode-novel"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Navigation buttons */}
        {!isMobile && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 w-12 h-12 rounded-full transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'}`}
              onClick={scrollLeft}
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 w-12 h-12 rounded-full transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'}`}
              onClick={scrollRight}
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </Button>
          </>
        )}

        {/* Horizontal scroll container */}
        <div 
          ref={scrollContainerRef}
          className="h-full overflow-x-auto overflow-y-hidden px-8 py-4"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <style>{`.horizontal-reader ::-webkit-scrollbar { display: none; }`}</style>
          
          {/* Multi-column text layout */}
          <div 
            className="h-full prose prose-invert prose-lg max-w-none prose-p:text-zinc-300"
            style={{
              columnWidth: '400px',
              columnGap: '60px',
              columnFill: 'auto',
              height: '100%',
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </div>

        {/* Info bar */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full">
          <span className="text-white text-sm">
            {content.wordCount?.toLocaleString() || '~'} words • Scroll horizontally
          </span>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
      <FileText className="w-16 h-16 text-zinc-600 mb-4" />
      <p className="text-zinc-400">Unable to display content in horizontal mode</p>
    </div>
  );
};

export default HorizontalMode;
